import axios from "axios";
import { config } from "@/config";

const REDIRECT_PARAM_KEY = "redirectUrl";

const isAllowedRedirectDomain = (hostname: string): boolean => {
  const normalized = hostname.toLowerCase();

  return config.allowedRedirectDomains.some(
    (allowedDomain) =>
      normalized === allowedDomain || normalized.endsWith(`.${allowedDomain}`)
  );
};

const getCandidateRedirectUrl = (): string => {
  const params = new URLSearchParams(window.location.search);

  const redirectUrl = params.get(REDIRECT_PARAM_KEY);
  if (!redirectUrl) {
    // return default redirect url if not present in the URL
    return config.redirectUrl;
  }

  return redirectUrl;
};

const buildSafeRedirectUrl = (): URL => {
  const candidate = getCandidateRedirectUrl();

  if (!candidate) {
    throw new Error("Redirect URL is not configured.");
  }

  let url: URL;

  try {
    url = new URL(candidate);
  } catch {
    throw new Error("Redirect URL is invalid.");
  }

  if (url.protocol !== "https:") {
    throw new Error("Redirect URL must use HTTPS.");
  }

  if (url.username || url.password) {
    throw new Error("Redirect URL must not contain credentials.");
  }

  if (url.port && url.port !== "443") {
    throw new Error("Redirect URL must use the default HTTPS port.");
  }

  if (!isAllowedRedirectDomain(url.hostname)) {
    throw new Error("Redirect domain is not allowed.");
  }

  return url;
};

export const loginWithGoogle = async (googleToken: string): Promise<void> => {
  try {
    const response = await axios.post(
      config.loginApiUrl,
      {
        type: "GOOGLE",
        google_token: googleToken,
        source: "backoffice",
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    // Debug: log the full response
    console.log("Login API response:", response.data);
    console.log("Full response object:", response);

    const { token } = response.data;

    if (token) {
      // Since we can't set cookies on a different domain from JavaScript,
      // we'll pass the token as a URL parameter so the backend at redirectUrl
      // can read it and set the cookie in its own domain
      const redirectUrlObj = buildSafeRedirectUrl();
      redirectUrlObj.searchParams.set("token", token);

      console.log("Redirecting to:", redirectUrlObj.toString());

      // Redirect with token as URL parameter
      // The backend at redirectUrl should read the token and set the cookie
      window.location.href = redirectUrlObj.toString();
    } else {
      throw new Error("No token received from server");
    }
  } catch (error) {
    console.error("Login error:", error);
    throw error;
  }
};
